<script setup lang="ts">
import type { CmsElementSubCategorySlider } from "~/composables/useCustom";
import { getCategoryRoute } from "@shopware/helpers";
import { useCategory, useShopwareContext } from "#imports";
import { ref, onMounted, computed } from 'vue';
import type { Schemas } from "#shopware";
import SharedImage from "~/components/shared/SharedImage.vue";

type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { category } = useCategory();
const { apiClient } = useShopwareContext();

const { data: categoryResponse, error } = await useAsyncData(
  "cmsNavigationSubCategories" + category.value?.id,
  async () => {
    if (!category.value?.id) {
      return {} as NavigationElement;
    }

    const response = await apiClient.invoke(
      "readNavigation post /navigation/{activeId}/{rootId}",
      {
        pathParams: {
          activeId: category.value.id,
          rootId: category.value.id,
        },
        headers: {
          "sw-include-seo-urls": true,
        },
        body: {
          associations: {
            children: {
              associations: {
                media: {},
                seoUrls: {},
              },
            },
            seoUrls: {},
          },
        },
      }
    );
    
    // The response structure might be response.data or response.data.data
    const categoryData = response?.data || response;
    return categoryData || ({} as NavigationElement);
  }
);

const subCategories = computed(
  () => categoryResponse?.value || ([] as NavigationElement[])
);

const { initScroller, setupScrollers } = useMenuScroller();

const isMounted = ref(false);

onMounted(() => {
  setTimeout(() => {
    isMounted.value = true;
  }, 400);
});

// Function to get staggered animation style based on index
const getStaggeredStyle = (index: number) => {
  return {
    '--index': index,
  };
};

</script>

<template>
  <div class="sub-categories-slider !mb-10 md:!mb-[60px]" 
    role="region"  
    :aria-label="$t('ariaLabelSubCategoryNavigation')"
    v-if="subCategories.length"
  >
    <nav role="navigation">
      <div class="container relative">
        <div class="pb-[1px] overflow-y-visible overflow-x-auto hide-scrollbar scroll-smooth" data-menu-scroller>
          <!-- Sub categories list -->
          <ul class="flex gap-2.5 md:gap-3 lg:gap-3.5 list-none">
            <li 
              :class="['inline-block w-auto fade-in-up staggered-item', { 'is-mounted': isMounted }]"
              :style="getStaggeredStyle(index)"
              v-for="(subCategory, index) in subCategories" :key="subCategory.id">
              <NuxtLink
                :to="formatLink(getCategoryRoute(subCategory))"
                class="inline-flex items-center border border-gray-500 rounded-full py-2 lg:py-2.5 px-4 lg:px-6 bg-white hover:bg-light-grey-2 hover:border-light-grey-2 transition-all duration-300 ease-in-out w-max h-[42px] sm:h-[46px] 3xl:h-[54px]">
                <SharedImage
                  v-if="subCategory.media?.url"
                  :src="subCategory.media?.url"
                  :sizes="{ desktop: '80x36', tablet: '80x36', mobile: '56x28' }"
                  :alt="subCategory.media?.alt ? subCategory.media?.alt : $t('mysofbed')"
                  :width="subCategory.media?.metaData?.width"
                  :height="subCategory.media?.metaData?.height"
                  loading="eager"
                  imgClass="w-14 h-7 lg:w-20 lg:h-9 object-contain mix-blend-multiply"
                  :mimeType="subCategory.media?.mimeType"
                />
                <span class="ml-1 font-mulish text-sm lg:text-base font-medium text-grey-600 whitespace-nowrap"
                  >{{ subCategory.name }}</span
                >
              </NuxtLink>
            </li>
          </ul>
        </div>
        <!-- Add scroll buttons -->
        <button
          data-scroll-left
          id="scrollLeftBtn"
          class="absolute h-[42px] sm:h-[46px] 3xl:h-[54px] w-[48px] lg:w-[90px] flex items-center justify-start left-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-white to-transparent from-30% p-2"
          :aria-label="$t('header.ariaLabelScrollMenuLeft')">
          <i class="icon-chevron-right rotate-180 text-md lg:text-lg text-primary"></i>
        </button>

        <button
          data-scroll-right
          id="scrollRightBtn"
          class="absolute h-[42px] sm:h-[46px] 3xl:h-[54px] w-[48px] lg:w-[90px] flex items-center justify-end right-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-l from-white to-transparent from-30% p-2"
          :aria-label="$t('header.ariaLabelScrollMenuRight')">
          <i class="icon-chevron-right text-md lg:text-lg text-primary"></i>
        </button>
      </div>
    </nav>
  </div>
</template>

<style scoped>
.fade-in-up {
  opacity: 0;
  transform: scale(0.95);
}

.fade-in-up.is-mounted {
  animation: fadeInUp 0.5s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.staggered-item.is-mounted {
  animation-delay: calc(var(--index) * 50ms);
}
</style>